import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchAllCustomers,
  fetchTodayCustomers,
  fetchTodayRegistrations,
  fetchRepeatCustomers,
  giveSignupBonus,
  type CustomerRecord,
} from '@/lib/supabase';

type CRTab = 'today_active' | 'today_reg' | 'cart' | 'repeat' | 'all';

interface RepeatCustomer {
  phone: string;
  name: string;
  order_count: number;
  total_spent: number;
  last_order: string;
}

interface CartCustomerEntry {
  phone: string;
  name: string;
  city?: string;
  cartItems: Array<{
    id: number;
    name: string;
    price: number;
    image: string;
    category: string;
    quantity: number;
    size?: string;
    selectedColor?: { name: string; hex: string };
  }>;
  cartTotal: number;
}

const TABS: { key: CRTab; label: string; icon: string; color: string }[] = [
  { key: 'today_active', label: 'Today Active', icon: 'ri-pulse-line', color: 'bg-green-500' },
  { key: 'today_reg', label: 'New Registered', icon: 'ri-user-add-line', color: 'bg-orange-500' },
  { key: 'cart', label: 'Cart Items', icon: 'ri-shopping-cart-2-line', color: 'bg-amber-500' },
  { key: 'repeat', label: 'Repeat Orders', icon: 'ri-repeat-2-line', color: 'bg-purple-500' },
  { key: 'all', label: 'All Customers', icon: 'ri-team-line', color: 'bg-gray-700' },
];

// ─── Signup Bonus Sound ────────────────────────────────────────────────────────
function playBonusSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const playTone = (freq: number, start: number, dur: number, type: 'sine' | 'square' | 'sawtooth' | 'triangle' = 'sine') => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
      gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    };
    // Cheerful ascending arpeggio
    playTone(523, 0, 0.15);
    playTone(659, 0.15, 0.15);
    playTone(784, 0.3, 0.15);
    playTone(1047, 0.45, 0.4);
    playTone(784, 0.65, 0.1);
    playTone(1047, 0.75, 0.5);
  } catch { /* ignore */ }
}

export default function CustomerRecordsPanel() {
  const [activeTab, setActiveTab] = useState<CRTab>('today_active');
  const [loading, setLoading] = useState(false);

  const [todayActive, setTodayActive] = useState<CustomerRecord[]>([]);
  const [todayReg, setTodayReg] = useState<CustomerRecord[]>([]);
  const [repeatCustomers, setRepeatCustomers] = useState<RepeatCustomer[]>([]);
  const [allCustomers, setAllCustomers] = useState<CustomerRecord[]>([]);
  const [cartCustomers, setCartCustomers] = useState<CartCustomerEntry[]>([]);

  const [search, setSearch] = useState('');
  const [bonusLoading, setBonusLoading] = useState<string | null>(null);
  const [bonusSuccess, setBonusSuccess] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Signup bonus popup state
  const [bonusPopup, setBonusPopup] = useState<{ customer: CustomerRecord } | null>(null);
  const bonusAudioDone = useRef(false);

  const showToast = useCallback((msg: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Load cart customers from localStorage ──────────────────────────────────
  const loadCartCustomers = useCallback(async (allCusts: CustomerRecord[]) => {
    // We look for matching phone numbers in customers table who have active cart items stored
    // Since cart is stored in browser localStorage, we can only show this for current device
    // For admin panel, we use the customer database to find phones, then check if they have cart data
    // This is best-effort — we show customers whose phones match known customers
    const entries: CartCustomerEntry[] = [];
    for (const cust of allCusts) {
      const cartKey = 'clothybazar_cart';
      // We can't access other users' localStorage, but we can show a note
      // Instead, show customers who have recently been active and may have carts
      // We'll enrich with a placeholder to show the feature
      void cartKey;
      void entries;
    }
    // Since cart data is per-browser, show a message explaining this
    setCartCustomers([]);
  }, []);

  const loadData = useCallback(async (tab: CRTab) => {
    setLoading(true);
    if (tab === 'today_active') {
      const data = await fetchTodayCustomers();
      setTodayActive(data);
    } else if (tab === 'today_reg') {
      const data = await fetchTodayRegistrations();
      setTodayReg(data);
    } else if (tab === 'repeat') {
      const data = await fetchRepeatCustomers();
      setRepeatCustomers(data);
    } else if (tab === 'all') {
      const data = await fetchAllCustomers();
      setAllCustomers(data);
    } else if (tab === 'cart') {
      const all = await fetchAllCustomers();
      await loadCartCustomers(all);
    }
    setLoading(false);
  }, [loadCartCustomers]);

  useEffect(() => {
    loadData(activeTab);
  }, [activeTab, loadData]);

  const handleGiveBonus = useCallback(async (customer: CustomerRecord) => {
    if (customer.signup_bonus_given) {
      showToast('Bonus already given to this customer!', 'error');
      return;
    }
    // Show popup with sound
    setBonusPopup({ customer });
    bonusAudioDone.current = false;
    playBonusSound();
    bonusAudioDone.current = true;
  }, [showToast]);

  const confirmGiveBonus = useCallback(async () => {
    if (!bonusPopup) return;
    const { customer } = bonusPopup;
    setBonusLoading(customer.id);
    const result = await giveSignupBonus(customer.id, customer.phone);
    setBonusLoading(null);
    if (result.success) {
      playBonusSound();
      setBonusSuccess(customer.id);
      setBonusPopup(null);
      showToast(`₹50 bonus given to ${customer.name}!`, 'success');
      // Refresh current tab data
      const updateCustomer = (prev: CustomerRecord[]) =>
        prev.map(c => c.id === customer.id ? { ...c, wallet_balance: c.wallet_balance + 50, signup_bonus_given: true } : c);
      setTodayActive(updateCustomer);
      setTodayReg(updateCustomer);
      setAllCustomers(updateCustomer);
      setTimeout(() => setBonusSuccess(null), 3000);
    } else {
      setBonusPopup(null);
      showToast(result.error ?? 'Error giving bonus', 'error');
    }
  }, [bonusPopup, showToast]);

  const handleGiveBonusAll = useCallback(async () => {
    const targets = todayReg.filter(c => !c.signup_bonus_given);
    if (targets.length === 0) { showToast('Bonus already given to all customers!', 'info'); return; }
    showToast(`Giving ₹50 bonus to ${targets.length} customers...`, 'info');
    for (const c of targets) {
      await giveSignupBonus(c.id, c.phone);
    }
    playBonusSound();
    showToast(`Signup bonus given to ${targets.length} new members!`, 'success');
    const data = await fetchTodayRegistrations();
    setTodayReg(data);
  }, [todayReg, showToast]);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const filteredAll = allCustomers.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name.toLowerCase().includes(q) || c.phone.includes(q) || (c.email ?? '').toLowerCase().includes(q);
  });

  const getTabCount = (tab: CRTab) => {
    if (tab === 'today_active') return todayActive.length;
    if (tab === 'today_reg') return todayReg.length;
    if (tab === 'repeat') return repeatCustomers.length;
    if (tab === 'all') return allCustomers.length;
    if (tab === 'cart') return cartCustomers.length;
    return 0;
  };

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap shadow-xl ${
          toast.type === 'success' ? 'bg-green-500' : toast.type === 'error' ? 'bg-red-500' : 'bg-gray-800'
        }`}>
          <i className={toast.type === 'success' ? 'ri-check-line' : toast.type === 'error' ? 'ri-close-line' : 'ri-information-line'}></i>
          {toast.msg}
        </div>
      )}

      {/* Signup Bonus Popup */}
      {bonusPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden">
            {/* Animated gradient header */}
            <div className="relative bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 px-6 pt-8 pb-6 text-center overflow-hidden">
              {/* Confetti dots */}
              <div className="absolute inset-0 pointer-events-none">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 rounded-full opacity-70 animate-bounce"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      backgroundColor: ['#fff', '#ffd700', '#ff69b4', '#00ff00', '#87ceeb'][i % 5],
                      animationDelay: `${Math.random() * 1}s`,
                      animationDuration: `${0.5 + Math.random() * 1}s`,
                    }}
                  />
                ))}
              </div>
              <div className="relative z-10">
                <div className="w-20 h-20 rounded-2xl bg-white/20 border-2 border-white/40 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className="ri-gift-2-fill text-white text-4xl"></i>
                </div>
                <h2 className="text-white font-bold text-2xl">₹50 Signup Bonus!</h2>
                <p className="text-white/80 text-sm mt-1">New member welcome reward</p>
              </div>
            </div>

            <div className="px-6 py-5">
              {/* Customer info */}
              <div className="bg-gray-50 rounded-2xl p-4 mb-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
                  {bonusPopup.customer.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-gray-900">{bonusPopup.customer.name}</p>
                  <p className="text-gray-500 text-sm">{bonusPopup.customer.phone}</p>
                  <p className="text-amber-600 text-xs font-semibold flex items-center gap-1 mt-0.5">
                    <i className="ri-wallet-line"></i>
                    Current balance: ₹{bonusPopup.customer.wallet_balance}
                  </p>
                </div>
              </div>

              {/* Bonus details */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 text-sm">Bonus Amount</span>
                  <span className="text-amber-600 font-bold text-lg">+₹50</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm">New Wallet Balance</span>
                  <span className="text-green-600 font-bold text-lg">₹{bonusPopup.customer.wallet_balance + 50}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setBonusPopup(null)}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-bold text-sm cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmGiveBonus}
                  disabled={bonusLoading === bonusPopup.customer.id}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm cursor-pointer hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {bonusLoading === bonusPopup.customer.id ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Giving...</>
                  ) : (
                    <><i className="ri-gift-2-fill"></i>Give ₹50 Bonus</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
            <i className="ri-team-line text-white text-lg"></i>
          </div>
          <div>
            <h2 className="text-gray-900 font-bold text-lg">Customer Records</h2>
            <p className="text-gray-400 text-xs">Active users, registrations, cart items, repeat orders</p>
          </div>
          <button
            onClick={() => loadData(activeTab)}
            className="ml-auto w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-all cursor-pointer"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-white rounded-2xl border border-gray-100 p-2 flex gap-1.5 flex-wrap">
        {TABS.map(tab => {
          const cnt = getTabCount(tab.key);
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                isActive ? `${tab.color} text-white` : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              {cnt > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${isActive ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      ) : (
        <>
          {/* ── TODAY ACTIVE ── */}
          {activeTab === 'today_active' && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
                  <i className="ri-pulse-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-green-800 font-bold">{todayActive.length} Active Customers Today</p>
                  <p className="text-green-600 text-xs">Logged in or used the app today</p>
                </div>
              </div>
              {todayActive.length === 0 ? (
                <EmptyState icon="ri-user-heart-line" text="No customers active today" />
              ) : (
                todayActive.map(c => (
                  <CustomerCard key={c.id} customer={c} onGiveBonus={handleGiveBonus} bonusSuccess={bonusSuccess} bonusLoading={bonusLoading} formatDate={formatDate} />
                ))
              )}
            </div>
          )}

          {/* ── TODAY REGISTRATIONS ── */}
          {activeTab === 'today_reg' && (
            <div className="space-y-3">
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center shrink-0">
                      <i className="ri-user-add-line text-white text-lg"></i>
                    </div>
                    <div>
                      <p className="text-orange-800 font-bold">{todayReg.length} New Members Today</p>
                      <p className="text-orange-600 text-xs">{todayReg.filter(c => !c.signup_bonus_given).length} bonus baaki hain</p>
                    </div>
                  </div>
                  {todayReg.some(c => !c.signup_bonus_given) && (
                    <button
                      onClick={handleGiveBonusAll}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-500 text-white font-bold text-xs cursor-pointer hover:bg-orange-600 transition-all whitespace-nowrap"
                    >
                      <i className="ri-gift-2-fill"></i>
                      Give ₹50 to All New Members
                    </button>
                  )}
                </div>
              </div>
              {todayReg.length === 0 ? (
                <EmptyState icon="ri-user-add-line" text="No new registrations today" />
              ) : (
                todayReg.map(c => (
                  <CustomerCard key={c.id} customer={c} onGiveBonus={handleGiveBonus} bonusSuccess={bonusSuccess} bonusLoading={bonusLoading} formatDate={formatDate} showBonusBtn />
                ))
              )}
            </div>
          )}

          {/* ── CART ITEMS ── */}
          {activeTab === 'cart' && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
                    <i className="ri-shopping-cart-2-line text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-amber-800 font-bold">Cart Items Visibility</p>
                    <p className="text-amber-700 text-sm leading-relaxed mt-1">
                      Customer ka cart data unke device ke local storage mein save hota hai — admin panel directly access nahi kar sakta. Lekin aap orders se dekh sakte hain ki kaun sa customer kya order karna chahta tha.
                    </p>
                    <p className="text-amber-600 text-xs mt-2 font-semibold">
                      Tip: "Repeat Orders" tab mein dekho — wahan frequent buyers hain jo baar baar order karte hain!
                    </p>
                  </div>
                </div>
              </div>

              {/* Show all customers with order history — potential cart abandoners */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <p className="text-gray-700 font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-history-line text-gray-500"></i>
                  Recent Active Customers (Possible Cart Abandoners)
                </p>
                <p className="text-gray-400 text-xs mb-4">Jin customers ne aaj app use kiya lekin order nahi kiya — unhe follow up karo</p>
                {todayActive.length === 0 ? (
                  <EmptyState icon="ri-shopping-cart-line" text="Aaj koi active customer nahi" />
                ) : (
                  todayActive.map(c => (
                    <div key={c.id} className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center font-bold text-gray-600 shrink-0 text-sm">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm">{c.name}</p>
                        <p className="text-gray-500 text-xs">{c.phone} · {c.address_city}</p>
                      </div>
                      <a
                        href={`https://wa.me/91${c.phone}?text=Hi ${encodeURIComponent(c.name)}, kuch cart mein reh gaya? Order place karo aur pao special cashback!`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-xl bg-green-50 border border-green-200 text-green-700 text-xs font-bold cursor-pointer hover:bg-green-100 transition-all whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-whatsapp-fill"></i>
                        Follow Up
                      </a>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── REPEAT ORDERS ── */}
          {activeTab === 'repeat' && (
            <div className="space-y-3">
              <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center shrink-0">
                  <i className="ri-repeat-2-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-purple-800 font-bold">{repeatCustomers.length} Repeat Customers</p>
                  <p className="text-purple-600 text-xs">2+ orders waale loyal customers</p>
                </div>
              </div>
              {repeatCustomers.length === 0 ? (
                <EmptyState icon="ri-repeat-line" text="Abhi tak koi repeat customer nahi" />
              ) : (
                repeatCustomers.map((c, i) => (
                  <div key={c.phone} className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold shrink-0"
                        style={{ background: `hsl(${(i * 47) % 360}, 60%, 55%)` }}>
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 text-sm">{c.name}</p>
                        <p className="text-gray-500 text-xs">{c.phone}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex items-center gap-1 justify-end">
                          <span className="px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">
                            {c.order_count} Orders
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-green-50 rounded-xl p-3 text-center">
                        <p className="text-green-800 font-bold text-base">₹{c.total_spent.toLocaleString('en-IN')}</p>
                        <p className="text-green-600 text-[11px]">Total Spent</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <p className="text-gray-800 font-bold text-base">₹{Math.round(c.total_spent / c.order_count).toLocaleString('en-IN')}</p>
                        <p className="text-gray-500 text-[11px]">Avg Order</p>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-gray-400 text-xs flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        Last order: {formatDate(c.last_order)}
                      </p>
                      <a
                        href={`https://wa.me/91${c.phone}?text=Hi ${encodeURIComponent(c.name)}, thank you for being our loyal customer! Aapke liye special offer hai!`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-xl bg-green-50 border border-green-200 text-green-700 text-xs font-bold cursor-pointer hover:bg-green-100 transition-all whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-whatsapp-fill"></i>
                        Message
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── ALL CUSTOMERS ── */}
          {activeTab === 'all' && (
            <div className="space-y-3">
              {/* Search */}
              <div className="bg-white rounded-2xl border border-gray-100 p-3">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by name, phone, email..."
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-200 outline-none"
                  />
                </div>
                <p className="text-gray-400 text-xs mt-2 px-1">Total: {allCustomers.length} customers · Showing: {filteredAll.length}</p>
              </div>

              {filteredAll.length === 0 ? (
                <EmptyState icon="ri-team-line" text="Koi customer nahi mila" />
              ) : (
                filteredAll.map(c => (
                  <CustomerCard key={c.id} customer={c} onGiveBonus={handleGiveBonus} bonusSuccess={bonusSuccess} bonusLoading={bonusLoading} formatDate={formatDate} showBonusBtn />
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Customer Card ─────────────────────────────────────────────────────────────
function CustomerCard({
  customer: c,
  onGiveBonus,
  bonusSuccess,
  bonusLoading,
  formatDate,
  showBonusBtn = false,
}: {
  customer: CustomerRecord;
  onGiveBonus: (c: CustomerRecord) => void;
  bonusSuccess: string | null;
  bonusLoading: string | null;
  formatDate: (s: string) => string;
  showBonusBtn?: boolean;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gray-800 to-gray-600 flex items-center justify-center text-white font-bold text-base shrink-0">
          {c.name.charAt(0).toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-gray-900 text-sm">{c.name}</p>
            {c.signup_bonus_given && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold flex items-center gap-1">
                <i className="ri-gift-fill text-[9px]"></i>Bonus Given
              </span>
            )}
          </div>
          <p className="text-gray-500 text-xs">{c.phone}{c.email ? ` · ${c.email}` : ''}</p>
          <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
            {c.address_city && <span className="flex items-center gap-1"><i className="ri-map-pin-line text-gray-400 text-[10px]"></i>{c.address_city}</span>}
            <span className="flex items-center gap-1"><i className="ri-calendar-line text-gray-400 text-[10px]"></i>Joined {formatDate(c.registered_at)}</span>
            {c.last_login_at && <span className="flex items-center gap-1"><i className="ri-login-circle-line text-gray-400 text-[10px]"></i>Active {formatDate(c.last_login_at)}</span>}
          </p>
        </div>

        {/* Wallet badge */}
        <div className="shrink-0 text-right">
          <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-xl px-2.5 py-1.5 mb-2">
            <i className="ri-wallet-line text-amber-500 text-sm"></i>
            <span className="text-amber-700 font-bold text-sm">₹{c.wallet_balance}</span>
          </div>

          {showBonusBtn && !c.signup_bonus_given && (
            <button
              onClick={() => onGiveBonus(c)}
              disabled={bonusLoading === c.id}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap ${
                bonusSuccess === c.id
                  ? 'bg-green-100 text-green-700 border border-green-300'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-md'
              } disabled:opacity-60`}
            >
              {bonusLoading === c.id ? (
                <><i className="ri-loader-4-line animate-spin"></i>Giving...</>
              ) : bonusSuccess === c.id ? (
                <><i className="ri-check-line"></i>₹50 Done!</>
              ) : (
                <><i className="ri-gift-2-fill"></i>₹50 Bonus</>
              )}
            </button>
          )}
        </div>
      </div>

      {/* WhatsApp quick action */}
      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2">
        <a
          href={`https://wa.me/91${c.phone}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-green-50 border border-green-200 text-green-700 text-xs font-bold cursor-pointer hover:bg-green-100 transition-all whitespace-nowrap"
        >
          <i className="ri-whatsapp-fill"></i>
          WhatsApp
        </a>
        <a
          href={`tel:${c.phone}`}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold cursor-pointer hover:bg-gray-100 transition-all whitespace-nowrap"
        >
          <i className="ri-phone-fill"></i>
          Call
        </a>
      </div>
    </div>
  );
}

function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 bg-white rounded-2xl border border-gray-100">
      <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
        <i className={`${icon} text-3xl text-gray-300`}></i>
      </div>
      <p className="text-gray-500 text-sm font-medium">{text}</p>
    </div>
  );
}
